import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  CheckCircle2,
  CircleX,
  Clock3,
  XCircle,
} from 'lucide-react';
import { useConfirm } from '../../contexts/ConfirmContext';
import { useToast } from '../../contexts/ToastContext';
import actionItemService from '../../services/actionItemService';
import ActionItemCommentsFeed from './ActionItemCommentsFeed';
import ActionItemStatusBadge from './ActionItemStatusBadge';
import './ActionItems.css';

const advanceLabelKeys = {
  APPROVED: 'actionItems.moveToOpen',
  OPEN: 'actionItems.moveToInProgress',
  IN_PROGRESS: 'actionItems.closeAction',
};

const ActionItemDetailPage = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const { confirm } = useConfirm();
  const toast = useToast();
  const [item, setItem] = useState(null);
  const [dueDateDraft, setDueDateDraft] = useState('');
  const [decisionComment, setDecisionComment] = useState('');
  const [cancellationNote, setCancellationNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [busyAction, setBusyAction] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    const fetchItem = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await actionItemService.get(id);
        if (!active) return;
        setItem(response);
        setDueDateDraft(response.due_date || '');
      } catch (requestError) {
        if (active) setError(t('actionItems.errors.detail'));
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchItem();
    return () => {
      active = false;
    };
  }, [id, t]);

  const displayValue = (value) => (
    value === null || value === undefined || value === ''
      ? t('actionItems.emptyValue')
      : value
  );

  const displayUser = (user) => {
    if (!user) return t('actionItems.emptyValue');
    const name = `${user.first_name || ''} ${user.last_name || ''}`.trim();
    return name || user.email || t('actionItems.emptyValue');
  };

  const displayStatus = (status) => (
    status ? t(`actionItems.status.${status}`) : t('actionItems.historyCreation')
  );

  const formatDateTime = (value) => {
    if (!value) return t('actionItems.emptyValue');
    return new Intl.DateTimeFormat(i18n.language, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(value));
  };

  const formatDate = (value) => {
    if (!value) return t('actionItems.emptyValue');
    return new Intl.DateTimeFormat(i18n.language, {
      dateStyle: 'medium',
      timeZone: 'UTC',
    }).format(new Date(`${value}T00:00:00Z`));
  };

  const saveDueDate = async () => {
    if (
      !item?.can_edit_due_date
      || dueDateDraft === (item.due_date || '')
    ) return;
    setBusyAction('dueDate');
    try {
      const updated = await actionItemService.updateDueDate(
        item.id,
        dueDateDraft || null,
      );
      setItem(updated);
      setDueDateDraft(updated.due_date || '');
      toast.success(t('actionItems.dueDateSaved'));
    } catch (requestError) {
      toast.error(
        requestError.response?.data?.detail
          || t('actionItems.errors.dueDate'),
      );
    } finally {
      setBusyAction('');
    }
  };

  const runAction = async (action) => {
    const requiresConfirmation = (
      action === 'disapprove'
      || action === 'cancel'
      || (action === 'advance' && item.status === 'IN_PROGRESS')
    );
    if (requiresConfirmation) {
      const accepted = await confirm(
        t(`actionItems.confirmations.${action}`),
        {
          title: t('common.confirm'),
          confirmText: t(`actionItems.actionLabels.${action}`),
          variant: action === 'advance' ? 'primary' : 'danger',
        },
      );
      if (!accepted) return;
    }

    const handlers = {
      approve: () => actionItemService.approve(id, decisionComment),
      disapprove: () => actionItemService.disapprove(id, decisionComment),
      advance: () => actionItemService.advance(id),
      cancel: () => actionItemService.cancel(id, cancellationNote),
    };

    setBusyAction(action);
    try {
      const updated = await handlers[action]();
      setItem(updated);
      setDueDateDraft(updated.due_date || '');
      setDecisionComment('');
      setCancellationNote('');
      toast.success(t('actionItems.actionCompleted'));
    } catch (requestError) {
      toast.error(
        requestError.response?.data?.detail
          || t('actionItems.errors.action'),
      );
    } finally {
      setBusyAction('');
    }
  };

  if (loading) {
    return (
      <div className="action-items-page" role="main">
        <div className="action-items-state">{t('common.loading')}</div>
      </div>
    );
  }

  if (error || !item) {
    return (
      <div className="action-items-page" role="main">
        <div className="action-items-alert" role="alert">
          {error || t('actionItems.errors.detail')}
        </div>
        <Link className="action-items-back-link" to="/action-items">
          <ArrowLeft size={17} aria-hidden="true" />
          {t('actionItems.backToList')}
        </Link>
      </div>
    );
  }

  const availableActions = item.available_actions || [];
  const showDecisionComment = (
    availableActions.includes('approve')
    || availableActions.includes('disapprove')
  );

  return (
    <div className="action-items-page" role="main">
      <header className="action-items-page-header action-item-detail-header">
        <div>
          <Link className="action-items-back-link" to="/action-items">
            <ArrowLeft size={17} aria-hidden="true" />
            {t('actionItems.backToList')}
          </Link>
          <div className="action-item-detail-title">
            <h1>{item.training_course}</h1>
            <ActionItemStatusBadge status={item.status} />
          </div>
          <p>{item.centre_details?.name || t('actionItems.emptyValue')}</p>
        </div>
      </header>

      <div className="action-item-detail-layout">
        <div className="action-item-detail-main">
          <section className="action-item-detail-card">
            <h2>{t('actionItems.form.details')}</h2>
            <dl className="action-item-readonly-grid">
              <div>
                <dt>{t('actionItems.trainingCourse')}</dt>
                <dd>{displayValue(item.training_course)}</dd>
              </div>
              <div>
                <dt>{t('actionItems.creationDate')}</dt>
                <dd>{formatDateTime(item.created_at)}</dd>
              </div>
              <div>
                <dt>{t('actionItems.location')}</dt>
                <dd>{displayValue(item.location)}</dd>
              </div>
              <div>
                <dt>{t('actionItems.accountable')}</dt>
                <dd>{displayValue(item.accountable)}</dd>
              </div>
              <div>
                <dt>{t('actionItems.dueDate')}</dt>
                <dd>
                  {item.can_edit_due_date ? (
                    <div className="action-item-due-date-editor">
                      <input
                        aria-label={t('actionItems.dueDate')}
                        type="date"
                        value={dueDateDraft}
                        disabled={busyAction === 'dueDate'}
                        onChange={(event) => (
                          setDueDateDraft(event.target.value)
                        )}
                      />
                      {dueDateDraft !== (item.due_date || '') && (
                        <button
                          className="action-items-primary-button"
                          type="button"
                          disabled={busyAction === 'dueDate'}
                          onClick={saveDueDate}
                        >
                          {t('actionItems.saveDueDate')}
                        </button>
                      )}
                    </div>
                  ) : formatDate(item.due_date)}
                </dd>
              </div>
              <div>
                <dt>{t('actionItems.closedDate')}</dt>
                <dd>{formatDateTime(item.closed_at)}</dd>
              </div>
              <div>
                <dt>{t('actionItems.filters.creator')}</dt>
                <dd>{displayUser(item.created_by_details)}</dd>
              </div>
              <div className="action-item-readonly--wide">
                <dt>{t('actionItems.gapProblemDetected')}</dt>
                <dd>{displayValue(item.gap_problem_detected)}</dd>
              </div>
              <div className="action-item-readonly--wide">
                <dt>{t('actionItems.correctiveAction')}</dt>
                <dd>{displayValue(item.corrective_action)}</dd>
              </div>
              <div className="action-item-readonly--wide">
                <dt>{t('actionItems.commentFeedback')}</dt>
                <dd>{displayValue(item.comment)}</dd>
              </div>
            </dl>
          </section>

          <section className="action-item-detail-card">
            <h2>{t('actionItems.costAndApproval')}</h2>
            <dl className="action-item-readonly-grid">
              <div>
                <dt>{t('actionItems.costExclTax')}</dt>
                <dd>{displayValue(item.cost)}</dd>
              </div>
              <div>
                <dt>{t('actionItems.currency')}</dt>
                <dd>{displayValue(item.currency)}</dd>
              </div>
              <div>
                <dt>{t('actionItems.approvalMode')}</dt>
                <dd>
                  {item.approval_mode === 'ALL'
                    ? t('actionItems.allApprovals')
                    : t('actionItems.oneApproval')}
                </dd>
              </div>
            </dl>
          </section>

          <section className="action-item-detail-card">
            <h2>{t('actionItems.approvals')}</h2>
            <div className="action-item-approval-list">
              {(item.approvals || []).map((approval) => (
                <div className="action-item-approval" key={approval.id}>
                  <span
                    className={`action-item-decision action-item-decision--${
                      approval.decision.toLowerCase()
                    }`}
                  >
                    {approval.decision === 'APPROVED' && (
                      <CheckCircle2 size={18} aria-hidden="true" />
                    )}
                    {approval.decision === 'DISAPPROVED' && (
                      <CircleX size={18} aria-hidden="true" />
                    )}
                    {approval.decision === 'PENDING' && (
                      <Clock3 size={18} aria-hidden="true" />
                    )}
                  </span>
                  <div>
                    <strong>{approval.manager_name}</strong>
                    <span>
                      {t(`actionItems.decisions.${approval.decision}`)}
                    </span>
                    {approval.comment && <p>{approval.comment}</p>}
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="action-item-detail-card">
            <h2>{t('common.history')}</h2>
            <ol className="action-item-history">
              {(item.status_history || []).map((history) => (
                <li key={history.id}>
                  <span className="action-item-history-dot" />
                  <div>
                    <strong>
                      {!history.from_status
                        ? t('actionItems.historyCreation')
                        : `${displayStatus(history.from_status)} → ${
                          displayStatus(history.to_status)
                        }`}
                    </strong>
                    {!history.from_status && (
                      <span>{displayStatus(history.to_status)}</span>
                    )}
                    <time dateTime={history.created_at}>
                      {formatDateTime(history.created_at)}
                    </time>
                    {history.note && <p>{history.note}</p>}
                  </div>
                </li>
              ))}
            </ol>
          </section>
        </div>

        <aside className="action-item-detail-sidebar">
          <ActionItemCommentsFeed
            itemId={item.id}
            canAdd={Boolean(item.can_add_comments_ac)}
          />

          {availableActions.length > 0 && (
            <section className="action-item-detail-card">
              <h2>{t('common.actions')}</h2>
              {showDecisionComment && (
                <label className="action-item-field">
                  <span>{t('actionItems.decisionComment')}</span>
                  <textarea
                    rows="3"
                    value={decisionComment}
                    onChange={(event) => (
                      setDecisionComment(event.target.value)
                    )}
                  />
                </label>
              )}
              {availableActions.includes('cancel') && (
                <label className="action-item-field">
                  <span>{t('actionItems.cancellationNote')}</span>
                  <textarea
                    rows="2"
                    value={cancellationNote}
                    onChange={(event) => (
                      setCancellationNote(event.target.value)
                    )}
                  />
                </label>
              )}
              <div className="action-item-action-list">
                {availableActions.includes('approve') && (
                  <button
                    className="action-items-primary-button"
                    type="button"
                    disabled={Boolean(busyAction)}
                    onClick={() => runAction('approve')}
                  >
                    <CheckCircle2 size={18} aria-hidden="true" />
                    {t('actionItems.approve')}
                  </button>
                )}
                {availableActions.includes('disapprove') && (
                  <button
                    className="action-items-danger-button"
                    type="button"
                    disabled={Boolean(busyAction)}
                    onClick={() => runAction('disapprove')}
                  >
                    <XCircle size={18} aria-hidden="true" />
                    {t('actionItems.disapprove')}
                  </button>
                )}
                {availableActions.includes('advance') && (
                  <button
                    className="action-items-primary-button"
                    type="button"
                    disabled={Boolean(busyAction)}
                    onClick={() => runAction('advance')}
                  >
                    {t(
                      advanceLabelKeys[item.status]
                        || 'actionItems.advance',
                    )}
                  </button>
                )}
                {availableActions.includes('cancel') && (
                  <button
                    className="action-items-danger-button"
                    type="button"
                    disabled={Boolean(busyAction)}
                    onClick={() => runAction('cancel')}
                  >
                    {t('actionItems.cancel')}
                  </button>
                )}
              </div>
            </section>
          )}
        </aside>
      </div>
    </div>
  );
};

export default ActionItemDetailPage;
